import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import {
    CheckCircle, Target, Lightbulb, Users, Shield, Rocket,
    Clock, Code, Zap, Calendar, Award, Briefcase, TrendingUp, Smile,
    ArrowRight, Globe, Cpu, Bot, Layout, Database
} from 'lucide-react';



import IndustriesSection from './components/IndustriesSection';
import CompanyOverview from './components/CompanyOverview';

const AboutUs = () => {
    const heroRef = useRef(null);
    const footerRef = useRef(null);
    const containerRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);


    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };


    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };


    // Marquee Animation
    const marqueeVariants = {
        animate: {
            x: [0, -1035],
            transition: {
                x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 20,
                    ease: "linear",
                },
            },
        },
    };


    const techStack = [
        { name: "React", logo: "https://placehold.co/150x50/png?text=React" },
        { name: "Node.js", logo: "https://placehold.co/150x50/png?text=Node.js" },
        { name: "Python", logo: "https://placehold.co/150x50/png?text=Python" },
        { name: "AWS", logo: "https://placehold.co/150x50/png?text=AWS" },
        { name: "Docker", logo: "https://placehold.co/150x50/png?text=Docker" },
        { name: "MongoDB", logo: "https://placehold.co/150x50/png?text=MongoDB" },
        { name: "Next.js", logo: "https://placehold.co/150x50/png?text=Next.js" },
        { name: "TypeScript", logo: "https://placehold.co/150x50/png?text=TypeScript" },
    ];


    const stats = [
        { label: "Commitment", value: "100%", icon: Target },
        { label: "Innovation", value: "Limitless", icon: Lightbulb },
        { label: "Support", value: "24/7", icon: Clock },
        { label: "Quality", value: "Premium", icon: Award },
    ];


    return (
        <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 mt-10 overflow-x-hidden" ref={containerRef}>
            <Header />


            <main className="flex-grow">
                <div className="">
                    {/* Enhanced Hero Section */}
                    <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white py-20 overflow-hidden">
                        {/* Animated Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
                            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                        </div>

                        <div className="container mx-auto relative z-10 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                className="max-w-4xl 3xl:max-w-5xl 4xl:max-w-6xl mx-auto text-center"
                            >
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.2, duration: 0.6 }}
                                    className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-3 py-1.5 sm:px-5 sm:py-2.5 3xl:px-6 3xl:py-3 rounded-full mb-4 sm:mb-6 border border-white/20"
                                >
                                    <motion.div
                                        animate={{
                                            scale: [1, 1.2, 1]
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        <Globe className="w-3.5 h-3.5 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 text-yellow-300" />
                                    </motion.div>
                                    <span className="text-[10px] sm:text-sm 3xl:text-base font-bold uppercase tracking-wider">Global IT Partner</span>
                                </motion.div>

                                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl 3xl:text-7xl font-bold mb-3 sm:mb-6 leading-tight tracking-tight">
                                    About Us
                                </h1>
                                <p className="text-lg md:text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl opacity-95 leading-relaxed max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto" style={{ lineHeight: '1.8' }}>
                                    ZovoX Technologies delivers AI automation, custom internal tools, and modern web applications designed to help businesses work smarter and grow faster.
                                </p>
                            </motion.div>
                        </div>
                    </section>
                    {/* 
                <CompanyOverview /> */}
                    {/* Who We Are Section */}
                    <section className="py-12 sm:py-16 3xl:py-24 4xl:py-32 bg-slate-50/50 relative overflow-hidden mt-5 mb-5">
                        {/* Background Decorations */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px] pointer-events-none">
                            <div className="absolute top-1/4 left-1/4 w-64 h-64 3xl:w-96 3xl:h-96 bg-blue-100/40 rounded-full blur-3xl mix-blend-multiply animate-blob" />
                            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 3xl:w-96 3xl:h-96 bg-purple-100/40 rounded-full blur-3xl mix-blend-multiply animate-blob animation-delay-2000" />
                        </div>

                        <div className="container mx-auto text-center mb-12 3xl:mb-20">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                                className="max-w-6xl 3xl:max-w-7xl mx-auto text-center"
                            >
                                <div className="flex justify-center mb-6 sm:mb-8">
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 3xl:w-24 3xl:h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-xl shadow-blue-500/20">
                                        <Users className="w-8 h-8 sm:w-10 sm:h-10 3xl:w-12 3xl:h-12 text-white" strokeWidth={2} />
                                    </div>
                                </div>
                                <h2 className="text-3xl sm:text-4xl md:text-5xl 3xl:text-6xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal py-1">
                                    Who We Are
                                </h2>
                                <div className="text-sm sm:text-base md:text-lg lg:text-xl 3xl:text-2xl text-slate-600 leading-loose lg:px-10 xl:px-20 2xl:px-25 3xl:px-32">
                                    <p>
                                        ZovoX Technologies is a forward-thinking technology solutions company specializing in <span className="font-bold text-slate-900">AI automation</span>, <span className="font-bold text-slate-900">custom internal tools</span>, and <span className="font-bold text-slate-900">modern web applications</span>. We help businesses eliminate manual work, overcome operational challenges, and scale with confidence.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 3xl:gap-10 mt-10 sm:mt-12 text-left">
                                    {/* AI Automation */}
                                    <motion.div
                                        className="group relative backdrop-blur-lg bg-white/60 border border-white/20 rounded-2xl p-5 sm:p-6 3xl:p-8 shadow-xl hover:shadow-2xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-2"
                                        whileHover="hover"
                                    >
                                        <div className="flex flex-col items-center text-center">
                                            <motion.div
                                                className="w-12 h-12 sm:w-14 sm:h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-purple-500/30"
                                                variants={{ hover: { scale: 1.1, rotate: [0, -10, 10, -10, 0] } }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <Bot className="w-6 h-6 sm:w-7 sm:h-7 3xl:w-8 3xl:h-8 text-white" strokeWidth={2.5} />
                                            </motion.div>
                                            <h3 className="font-bold text-slate-900 text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl mb-2">AI Automation</h3>
                                            <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-slate-600">Intelligent workflows that save time and reduce errors.</p>
                                        </div>
                                    </motion.div>

                                    {/* Custom Internal Tools */}
                                    <motion.div
                                        className="group relative backdrop-blur-lg bg-white/60 border border-white/20 rounded-2xl p-5 sm:p-6 3xl:p-8 shadow-xl hover:shadow-2xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-2"
                                        whileHover="hover"
                                    >
                                        <div className="flex flex-col items-center text-center">
                                            <motion.div
                                                className="w-12 h-12 sm:w-14 sm:h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30"
                                                variants={{ hover: { scale: 1.1, rotate: [0, -10, 10, -10, 0] } }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <Database className="w-6 h-6 sm:w-7 sm:h-7 3xl:w-8 3xl:h-8 text-white" strokeWidth={2.5} />
                                            </motion.div>
                                            <h3 className="font-bold text-slate-900 text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl mb-2">Internal Tools</h3>
                                            <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-slate-600">Custom systems tailored to streamline your operations.</p>
                                        </div>
                                    </motion.div>

                                    {/* Modern Web Applications */}
                                    <motion.div
                                        className="group relative backdrop-blur-lg bg-white/60 border border-white/20 rounded-2xl p-5 sm:p-6 3xl:p-8 shadow-xl hover:shadow-2xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-2"
                                        whileHover="hover"
                                    >
                                        <div className="flex flex-col items-center text-center">
                                            <motion.div
                                                className="w-12 h-12 sm:w-14 sm:h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30"
                                                variants={{ hover: { scale: 1.1, rotate: [0, -10, 10, -10, 0] } }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <Code className="w-6 h-6 sm:w-7 sm:h-7 3xl:w-8 3xl:h-8 text-white" strokeWidth={2.5} />
                                            </motion.div>
                                            <h3 className="font-bold text-slate-900 text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl mb-2">Web Applications</h3>
                                            <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-slate-600">Scalable, high-performance digital experiences.</p>
                                        </div>
                                    </motion.div>
                                </div>
                            </motion.div>
                        </div>
                    </section>

                    {/* Why ZovoX Section */}
                    <section className="py-20 3xl:py-32 bg-white overflow-hidden border-t border-gray-200">
                        <div className="container mx-auto max-w-7xl 3xl:max-w-[1400px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
                            {/* Top Section: Heading Only */}
                            <motion.div
                                className="mb-12 sm:mb-20 max-w-4xl 3xl:max-w-5xl mx-auto text-center"
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeInUp}
                            >
                                <h2 className="text-3xl sm:text-4xl md:text-5xl 3xl:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal py-1">
                                    Why Choose ZovoX Technologies?
                                </h2>
                            </motion.div>

                            <div className="grid lg:grid-cols-2 gap-12 sm:gap-20 3xl:gap-20 items-center">
                                {/* Left Column: Description & Feature Grid */}
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    variants={fadeInUp}
                                >
                                    <p className="text-slate-600 text-base sm:text-lg md:text-xl xl:text-2xl 3xl:text-2xl 4xl:text-3xl leading-relaxed mb-8 sm:mb-12">
                                        We blend technical expertise with real business understanding to build solutions that create measurable impact. We don’t just deliver code — we build digital assets that strengthen your business.
                                    </p>

                                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6 sm:gap-y-8 3xl:gap-x-10 3xl:gap-y-10">
                                        {[
                                            { icon: Zap, text: 'Agile & Modern Approach' },
                                            { icon: Code, text: 'Strong technical expertise' },
                                            { icon: Zap, text: 'Modern tech stack' },
                                            { icon: Target, text: 'Custom-made solutions' },
                                            { icon: CheckCircle, text: 'On-time delivery' },
                                            { icon: Users, text: 'Long-term support' }
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 3xl:w-12 3xl:h-12 bg-blue-50 rounded-full flex items-center justify-center">
                                                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 text-blue-600" />
                                                </div>
                                                <span className="text-slate-700 font-medium text-base sm:text-lg 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl">{item.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>


                                {/* Right Column: CTA Card */}
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    variants={fadeInUp}
                                    className="relative"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-2xl transform rotate-3 opacity-20" />
                                    <div className="bg-gradient-to-br from-blue-600 to-purple-700 p-6 sm:p-8 3xl:p-10 rounded-2xl relative text-white shadow-2xl">
                                        <h3 className="text-xl sm:text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-4xl 6xl:text-5xl font-bold mb-4 sm:mb-6">Ready to Transform Your Business?</h3>
                                        <p className="text-blue-100 mb-6 sm:mb-8 text-sm sm:text-base 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-2xl">
                                            Let's discuss how we can help you achieve your digital goals with our custom solutions.
                                        </p>
                                        <div className="space-y-4 3xl:space-y-6">
                                            <div className="flex items-center gap-4 p-4 3xl:p-5 bg-white/10 rounded-lg border border-white/20 hover:bg-white/20 transition-colors">
                                                <div className="w-10 h-10 3xl:w-12 3xl:h-12 bg-white/20 rounded-full flex items-center justify-center">
                                                    <Rocket className="w-5 h-5 3xl:w-6 3xl:h-6 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-sm sm:text-base 3xl:text-lg 4xl:text-xl">Scalable Solutions</h4>
                                                    <p className="text-xs sm:text-sm 3xl:text-base text-blue-100">Built to grow with your business</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 p-4 3xl:p-5 bg-white/10 rounded-lg border border-white/20 hover:bg-white/20 transition-colors">
                                                <div className="w-10 h-10 3xl:w-12 3xl:h-12 bg-white/20 rounded-full flex items-center justify-center">
                                                    <Shield className="w-5 h-5 3xl:w-6 3xl:h-6 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-sm sm:text-base 3xl:text-lg 4xl:text-xl">Secure & Reliable</h4>
                                                    <p className="text-xs sm:text-sm 3xl:text-base text-blue-100">Enterprise-grade security standards</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </section>

                    {/* Industries We Serve Section */}
                    <IndustriesSection />



                    {/* Tech Stack Marquee Section */}
                    <section className="bg-white border-b border-slate-100 overflow-hidden">
                        <div className="container mx-auto mb-10 text-center max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
                            <p className="text-base font-bold text-slate-900 uppercase tracking-widest 3xl:text-xl">Our Tech Stack</p>
                        </div>


                        <div className="relative flex overflow-x-hidden group max-w-[95%] xl:max-w-[90%] 3xl:max-w-[80%] mx-auto">
                            <motion.div
                                className="flex space-x-16 sm:space-x-24 3xl:space-x-32 animate-marquee whitespace-nowrap py-6 items-center"
                                variants={marqueeVariants}
                                animate="animate"
                            >
                                {[...techStack, ...techStack, ...techStack].map((tech, index) => (
                                    <div key={index} className="flex items-center gap-3 opacity-60 hover:opacity-100 transition-all duration-300 cursor-pointer grayscale hover:grayscale-0 transform hover:scale-110">
                                        {/* Using a more logo-like font style */}
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 3xl:w-12 3xl:h-12 bg-slate-900 rounded-lg flex items-center justify-center text-white font-bold text-lg 3xl:text-2xl">
                                                {tech.name.charAt(0)}
                                            </div>
                                            <span className="text-xl sm:text-2xl 3xl:text-3xl font-bold text-slate-800 font-sans tracking-tight">{tech.name}</span>
                                        </div>
                                    </div>
                                ))}
                            </motion.div>
                            {/* Gradient Overlays for smooth fade effect - Enhanced */}
                            <div className="absolute top-0 left-0 w-20 sm:w-40 h-full bg-gradient-to-r from-white via-white/80 to-transparent z-10" />
                            <div className="absolute top-0 right-0 w-20 sm:w-40 h-full bg-gradient-to-l from-white via-white/80 to-transparent z-10" />
                        </div>
                    </section>



                    {/* Mission & Vision Section */}
                    <section className="py-12 sm:py-20 3xl:py-32 bg-slate-50">
                        <div className="container mx-auto max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
                            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 3xl:gap-20 max-w-7xl 3xl:max-w-full mx-auto">
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    variants={fadeInUp}
                                    className="bg-white p-6 sm:p-8 3xl:p-12 rounded-2xl border border-slate-100 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden group"
                                >
                                    <div className="absolute top-0 right-0 w-32 h-32 3xl:w-48 3xl:h-48 bg-blue-50 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
                                    <div className="w-12 h-12 3xl:w-16 3xl:h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6 relative z-10">
                                        <Target className="w-6 h-6 3xl:w-8 3xl:h-8 text-blue-600" />
                                    </div>
                                    <h2 className="text-3xl sm:text-4xl md:text-5xl 3xl:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent relative z-10 leading-normal py-1">Our Mission</h2>
                                    <p className="text-slate-600 text-base sm:text-lg 3xl:text-xl leading-relaxed relative z-10">
                                        To empower businesses with intelligent digital solutions that enhance efficiency, reduce operational costs, and accelerate sustainable growth.
                                    </p>
                                </motion.div>


                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    variants={fadeInUp}
                                    className="bg-white p-6 sm:p-8 3xl:p-12 rounded-2xl border border-slate-100 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden group"
                                >
                                    <div className="absolute top-0 right-0 w-32 h-32 3xl:w-48 3xl:h-48 bg-purple-50 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
                                    <div className="w-12 h-12 3xl:w-16 3xl:h-16 bg-purple-100 rounded-xl flex items-center justify-center mb-6 relative z-10">
                                        <Lightbulb className="w-6 h-6 3xl:w-8 3xl:h-8 text-purple-600" />
                                    </div>
                                    <h2 className="text-3xl sm:text-4xl md:text-5xl 3xl:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent relative z-10 leading-normal py-1">Our Vision</h2>
                                    <p className="text-slate-600 text-base sm:text-lg 3xl:text-xl leading-relaxed relative z-10">
                                        To become a trusted technology partner for SMEs across India and beyond — delivering innovation, reliability, and long-term value.
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </section>



                    {/* Stats Section (Limitless Cards) */}
                    <section className="py-12 sm:py-20 3xl:py-32 bg-white relative overflow-hidden">
                        <div className="container mx-auto relative z-10 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
                            {/* Mobile Layout - Individual Cards (below 992px) */}
                            <div className="lg:hidden grid grid-cols-1 gap-4">
                                {stats.map((stat, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="p-6 sm:p-8 text-center group hover:bg-slate-50 transition-all duration-300 bg-white border border-slate-200 rounded-2xl shadow-lg hover:shadow-xl"
                                    >
                                        <div className="mb-4 sm:mb-6 inline-flex p-4 rounded-2xl bg-blue-50 text-blue-600 group-hover:scale-110 transition-transform duration-300">
                                            <stat.icon className="w-6 h-6 sm:w-8 sm:h-8" />
                                        </div>
                                        <div className="text-2xl sm:text-3xl sm:text-4xl font-bold mb-2 sm:mb-3 text-slate-900 tracking-tight">{stat.value}</div>
                                        <div className="text-[10px] sm:text-[11px] sm:text-xs font-bold uppercase tracking-widest text-slate-500">{stat.label}</div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Desktop Layout - Connected Cards (992px and above) */}
                            <div className="hidden lg:grid grid-cols-4 divide-x divide-slate-100 border border-slate-100 bg-white rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/50">
                                {stats.map((stat, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="p-8 lg:p-10 3xl:p-12 4xl:p-14 text-center group hover:bg-slate-50 transition-colors duration-300"
                                    >
                                        <div className="mb-4 lg:mb-6 inline-flex p-3 lg:p-4 3xl:p-5 rounded-2xl bg-blue-50 text-blue-600 group-hover:scale-110 transition-transform duration-300">
                                            <stat.icon className="w-6 h-6 lg:w-8 lg:h-8 3xl:w-10 3xl:h-10" />
                                        </div>
                                        <div className="text-4xl lg:text-5xl 3xl:text-6xl font-bold mb-3 text-slate-900 tracking-tight">
                                            {stat.value}
                                        </div>
                                        <div className="text-xs lg:text-sm 3xl:text-base font-bold uppercase tracking-widest text-slate-500">
                                            {stat.label}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                        </div>
                    </section>
                </div>
            </main>


            <Footer ref={footerRef} />
        </div >
    );
};


export default AboutUs;
